import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
  ChevronDown,
  Download,
  FileSearch,
  Link2,
  Link2Off,
  Search,
  ShieldCheck,
  ShieldX,
} from 'lucide-react';
import clsx from 'clsx';
import {
  AUDIT_ACTIONS,
  AUDIT_SEVERITIES,
  type AuditEntry,
  type AuditSeverity,
  type ChainVerificationResult,
  type SessionUser,
} from '@idas/shared';
import { api, ApiError } from '../lib/api';
import { EmptyState, ListSkeleton } from '../components/ui/EmptyState';
import { InlineSpinner, RefreshingOverlay } from '../components/ui/Refreshing';
import { toast } from '../components/ui/Toast';

const SEVERITY_STYLES: Record<AuditSeverity, string> = {
  INFO: 'border-edge bg-surface-sunken text-ink-muted',
  NOTICE: 'border-accent/30 bg-accent/10 text-accent',
  WARNING: 'border-warn/30 bg-warn/10 text-warn',
  CRITICAL: 'border-danger/30 bg-danger/10 text-danger',
};

/** Grouped so the action filter is navigable rather than a wall of 30 options. */
const ACTION_GROUPS = {
  'Data leaving the system': AUDIT_ACTIONS.filter(
    (a) => a.startsWith('EXPORT_') || a === 'RESULT_VIEWED',
  ),
  Queries: AUDIT_ACTIONS.filter((a) => a.startsWith('QUERY_')),
  Authentication: AUDIT_ACTIONS.filter((a) => a.startsWith('AUTH_')),
  Administration: AUDIT_ACTIONS.filter(
    (a) => a.startsWith('POLICY_') || a.startsWith('DATASOURCE_') || a.startsWith('USER_'),
  ),
  'Catalog & integrity': AUDIT_ACTIONS.filter(
    (a) => a.startsWith('CATALOG_') || a.startsWith('AUDIT_'),
  ),
} as const;

export function AuditPage({ user }: { user: SessionUser }) {
  const [action, setAction] = useState('');
  const [severity, setSeverity] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const summary = useQuery({ queryKey: ['audit', 'summary'], queryFn: () => api.audit.summary(7) });

  const log = useInfiniteQuery({
    queryKey: ['audit', 'log', action, severity, search, from],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.audit.list({
        action: action || undefined,
        severity: severity || undefined,
        search: search || undefined,
        from: from ? new Date(from).toISOString() : undefined,
        cursor: pageParam,
        limit: '100',
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const entries = log.data?.pages.flatMap((page) => page.entries) ?? [];

  const toggle = (seq: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl space-y-4 p-4">
          {/* ── Integrity ────────────────────────────────────────────────── */}
          {user.permissions.includes('audit:verify') && <ChainPanel />}

          {/* ── Last 7 days ──────────────────────────────────────────────── */}
          {summary.data && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat
                label="Queries run"
                value={(summary.data.totals.SUCCESS ?? 0).toLocaleString()}
                hint="last 7 days"
              />
              <Stat
                label="Blocked by policy"
                value={summary.data.denials.toLocaleString()}
                hint="refused before execution"
                tone={summary.data.denials > 0 ? 'warn' : undefined}
              />
              <Stat
                label="Exports"
                value={summary.data.exports.toLocaleString()}
                hint="data taken out of the tool"
                tone={summary.data.exports > 0 ? 'lock' : undefined}
              />
              <Stat
                label="Failed"
                value={(
                  (summary.data.totals.ERROR ?? 0) + (summary.data.totals.TIMEOUT ?? 0)
                ).toLocaleString()}
                hint="errors and timeouts"
              />
            </div>
          )}

          {summary.data && summary.data.topUsers.length > 0 && (
            <section className="panel rounded-lg">
              <header className="border-b border-edge px-3 py-2">
                <h2 className="text-xs font-semibold text-ink">Most active, last 7 days</h2>
              </header>
              <div className="divide-y divide-edge">
                {summary.data.topUsers.map((person) => (
                  <div key={person.userId} className="flex items-center gap-3 px-3 py-1.5">
                    <span className="min-w-0 flex-1 truncate text-2xs text-ink">{person.name}</span>
                    <span className="truncate text-2xs text-ink-faint">{person.email}</span>
                    <span className="w-20 text-right text-2xs tabular-nums text-ink-muted">
                      {person.queries.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── The log ──────────────────────────────────────────────────── */}
          <section className="panel rounded-lg">
            <header className="flex flex-wrap items-center gap-2 border-b border-edge px-3 py-2">
              <FileSearch className="h-3.5 w-3.5 text-ink-muted" />
              <h2 className="text-xs font-semibold text-ink">Audit log</h2>

              <div className="flex-1" />

              <div className="relative w-44">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-ink-faint" />
                <input
                  className="input py-1 pl-7 pr-7"
                  placeholder="Name, email, resource"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                {log.isFetching && !log.isLoading && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2">
                    <InlineSpinner />
                  </span>
                )}
              </div>

              <input
                className="input w-auto py-1"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
                title="Show events from this date onward"
              />

              <select
                className="input w-auto py-1"
                value={severity}
                onChange={(event) => setSeverity(event.target.value)}
              >
                <option value="">All severities</option>
                {AUDIT_SEVERITIES.map((level) => (
                  <option key={level} value={level}>
                    {level.toLowerCase()}
                  </option>
                ))}
              </select>

              <select
                className="input w-auto max-w-52 py-1"
                value={action}
                onChange={(event) => setAction(event.target.value)}
              >
                <option value="">All actions</option>
                {Object.entries(ACTION_GROUPS).map(([group, actions]) => (
                  <optgroup key={group} label={group}>
                    {actions.map((value) => (
                      <option key={value} value={value}>
                        {value.replace(/_/g, ' ').toLowerCase()}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </header>

            {log.isLoading && <ListSkeleton rows={8} height={40} />}

            {!log.isLoading && entries.length === 0 && (
              <EmptyState
                icon={FileSearch}
                title="No matching events"
                body="Try widening the date range or clearing the action filter. Every sign-in, query and export is recorded, so an empty result usually means the filter is too narrow."
              />
            )}

            {/* Filter changes replace the whole list, so the old rows stay
                put and dim rather than collapsing to a skeleton — losing your
                place on every keystroke is worse than a short stale moment. */}
            <RefreshingOverlay active={log.isFetching && !log.isLoading}>
              <div className="divide-y divide-edge">
                {entries.map((entry) => (
                  <AuditRow
                    key={entry.seq}
                    entry={entry}
                    expanded={expanded.has(entry.seq)}
                    onToggle={() => toggle(entry.seq)}
                  />
                ))}
              </div>
            </RefreshingOverlay>

            {log.hasNextPage && (
              <div className="border-t border-edge p-2 text-center">
                <button
                  className="btn"
                  onClick={() => void log.fetchNextPage()}
                  disabled={log.isFetchingNextPage}
                >
                  {log.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

/**
 * Chain integrity.
 *
 * Given prominence because a tamper-evident log that nobody verifies proves
 * nothing at all. The hash chain makes alteration *detectable*, not
 * impossible — detection only happens if someone runs this.
 */
function ChainPanel() {
  const [result, setResult] = useState<ChainVerificationResult | null>(null);

  const verify = useMutation({
    mutationFn: api.audit.verify,
    onSuccess: (outcome) => {
      setResult(outcome);
      if (outcome.ok) {
        toast.success(`${outcome.entriesChecked.toLocaleString()} entries verified intact.`);
      } else {
        toast.error(`Chain broken at entry ${outcome.brokenAt?.seq}. History has been altered.`);
      }
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Verification could not run.'),
  });

  const broken = result && !result.ok;

  return (
    <section
      className={clsx(
        'flex flex-wrap items-center gap-3 rounded-lg border p-3',
        broken
          ? 'border-danger/40 bg-danger/5'
          : result
            ? 'border-ok/30 bg-ok/5'
            : 'border-edge bg-surface-raised',
      )}
    >
      {broken ? (
        <Link2Off className="h-4 w-4 shrink-0 text-danger" />
      ) : (
        <Link2 className={clsx('h-4 w-4 shrink-0', result ? 'text-ok' : 'text-ink-muted')} />
      )}

      <div className="min-w-0 flex-1">
        <p className="text-2xs font-semibold text-ink">
          {broken
            ? 'Audit chain is broken'
            : result
              ? 'Audit chain verified'
              : 'Audit chain integrity'}
        </p>
        <p className="mt-0.5 text-2xs leading-relaxed text-ink-muted">
          {broken ? (
            <>
              Entry <code className="font-mono">{result.brokenAt?.seq}</code> from{' '}
              {result.brokenAt && new Date(result.brokenAt.timestamp).toLocaleString()} does not
              match its recorded hash. Something has been changed or removed directly in the
              database. Treat this as a security incident.
            </>
          ) : result ? (
            <>
              {result.entriesChecked.toLocaleString()} entries recomputed in {result.durationMs}ms.
              Every hash matches, so no historic record has been altered.
            </>
          ) : (
            'Each entry is hashed together with the one before it. Recomputing the chain proves no record has been edited or deleted — including by someone with direct database access.'
          )}
        </p>
      </div>

      <button className="btn shrink-0" onClick={() => verify.mutate()} disabled={verify.isPending}>
        <ShieldCheck className="h-3.5 w-3.5" />
        {verify.isPending ? 'Verifying…' : 'Verify now'}
      </button>
    </section>
  );
}

function AuditRow({
  entry,
  expanded,
  onToggle,
}: {
  entry: AuditEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  const isExport = entry.action.startsWith('EXPORT_');
  const isDenial = entry.action === 'QUERY_DENIED' || entry.action === 'AUTH_SSO_REJECTED';
  const sql = typeof entry.detail.sql === 'string' ? entry.detail.sql : null;

  return (
    <div className={clsx(entry.severity === 'CRITICAL' && 'bg-danger/[0.04]')}>
      <button
        className="flex w-full flex-wrap items-center gap-2.5 px-3 py-1.5 text-left hover:bg-surface-sunken"
        onClick={onToggle}
      >
        <ChevronDown
          className={clsx(
            'h-3 w-3 shrink-0 text-ink-faint transition-transform',
            expanded && 'rotate-180',
          )}
        />

        <span className="w-16 shrink-0 font-mono text-2xs tabular-nums text-ink-faint">
          {entry.seq}
        </span>

        <span className="w-36 shrink-0 text-2xs tabular-nums text-ink-muted">
          {new Date(entry.timestamp).toLocaleString()}
        </span>

        <span className={clsx('chip w-16 shrink-0 justify-center', SEVERITY_STYLES[entry.severity])}>
          {entry.severity.toLowerCase()}
        </span>

        <span className="flex w-52 shrink-0 items-center gap-1 text-2xs font-medium text-ink">
          {isExport && <Download className="h-3 w-3 text-lock" />}
          {isDenial && <ShieldX className="h-3 w-3 text-warn" />}
          {entry.action.replace(/_/g, ' ').toLowerCase()}
        </span>

        <span className="min-w-0 flex-1 truncate text-2xs text-ink-muted">
          {entry.actor.name ?? entry.actor.email ?? 'system'}
          {sql && <span className="ml-2 font-mono text-ink-faint">{sql.slice(0, 80)}</span>}
        </span>

        {entry.ip && (
          <span className="shrink-0 font-mono text-2xs text-ink-faint">{entry.ip}</span>
        )}
      </button>

      {expanded && (
        <div className="space-y-2 border-t border-edge bg-surface-sunken px-3 py-2.5 pl-12">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-4">
            <Detail label="Actor" value={entry.actor.email ?? '—'} />
            <Detail label="Role" value={entry.actor.role ?? '—'} />
            <Detail label="Resource" value={entry.resource ?? '—'} />
            <Detail label="Session" value={entry.sessionId ?? '—'} />
          </div>

          {sql && (
            <div>
              <p className="mb-1 text-2xs font-medium text-ink-muted">Statement</p>
              <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded border border-edge bg-surface p-2 font-mono text-2xs leading-relaxed text-ink">
                {sql}
              </pre>
            </div>
          )}

          {Object.keys(entry.detail).length > 0 && (
            <div>
              <p className="mb-1 text-2xs font-medium text-ink-muted">Detail</p>
              <pre className="max-h-64 overflow-auto rounded border border-edge bg-surface p-2 font-mono text-2xs leading-relaxed text-ink-muted">
                {JSON.stringify(
                  // sql is rendered above in a readable block; repeating it
                  // here as an escaped one-liner just adds noise.
                  Object.fromEntries(Object.entries(entry.detail).filter(([k]) => k !== 'sql')),
                  null,
                  2,
                )}
              </pre>
            </div>
          )}

          <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-edge pt-2">
            <Detail label="User agent" value={entry.userAgent ?? '—'} mono={false} />
            <Detail label="Hash" value={`${entry.hash.slice(0, 24)}…`} />
            <Detail label="Chains from" value={`${entry.prevHash.slice(0, 24)}…`} />
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, mono = true }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-2xs text-ink-faint">{label}</p>
      <p className={clsx('truncate text-2xs text-ink', mono && 'font-mono')} title={value}>
        {value}
      </p>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  tone?: 'warn' | 'lock';
}) {
  return (
    <div className="panel rounded-lg p-3">
      <p className="text-2xs text-ink-muted">{label}</p>
      <p
        className={clsx(
          'mt-1 text-xl font-semibold tabular-nums',
          tone === 'warn' ? 'text-warn' : tone === 'lock' ? 'text-lock' : 'text-ink',
        )}
      >
        {value}
      </p>
      <p className="mt-0.5 text-2xs text-ink-faint">{hint}</p>
    </div>
  );
}
